import React from 'react';
import { X, Send, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { listAttendees, broadcastToEvent } from '../lib/rsvp';

export default function BroadcastModal({ open, onClose, event }) {
  const [msg, setMsg] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [attendees, setAttendees] = React.useState(null);

  React.useEffect(() => {
    if (open && event) {
      listAttendees(event.id).then((d) => setAttendees(d)).catch(() => setAttendees({ attendees: [], count: 0 }));
    }
  }, [open, event]);

  if (!open || !event) return null;

  const send = async () => {
    if (!msg.trim()) return toast.error('Type a message');
    try {
      setSending(true);
      const { sent } = await broadcastToEvent(event.id, msg.trim());
      toast.success(`Sent to ${sent} attendee${sent === 1 ? '' : 's'}`);
      setMsg('');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="broadcast-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0d0d0d] hairline p-6 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-white" data-testid="broadcast-close"><X className="w-5 h-5" /></button>
        <div className="inline-flex items-center gap-2 am-pill"><Users className="w-3.5 h-3.5 text-[#ff2a2a]" /> Message everyone</div>
        <h2 className="mt-3 font-display text-2xl uppercase tracking-tight leading-tight">{event.title}</h2>

        {attendees === null ? (
          <div className="mt-4 text-xs font-mono uppercase tracking-widest text-zinc-500">Loading attendees…</div>
        ) : attendees.count === 0 ? (
          <p className="mt-4 text-sm text-zinc-400">No RSVPs yet — share the event link to get some.</p>
        ) : (
          <>
            <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="attendee-avatars">
              {attendees.attendees.slice(0, 8).map((a) => (
                a.picture ? (
                  <img key={a.user_id} src={a.picture} alt={a.name || 'Guest'} title={a.name || 'Guest'} className="w-8 h-8 rounded-full object-cover hairline" />
                ) : (
                  <div key={a.user_id} title={a.name || 'Guest'} className="w-8 h-8 rounded-full bg-[#ff2a2a] text-white grid place-items-center text-xs font-semibold">{(a.name || 'Guest')[0].toUpperCase()}</div>
                )
              ))}
              {attendees.count > 8 && <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">+{attendees.count - 8}</div>}
            </div>
            <textarea
              rows={4}
              value={msg}
              onChange={(e) => setMsg(e.target.value.slice(0, 2000))}
              placeholder="Announcement, weather update, parking tips…"
              className="mt-4 w-full bg-[#0d0d0d] hairline px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]"
              data-testid="broadcast-msg"
            />
            <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-600 flex justify-between">
              <span>{msg.length} / 2000</span>
              <span>Delivered as in-app notification</span>
            </div>
            <button
              onClick={send}
              disabled={sending || !msg.trim()}
              className="mt-4 w-full bg-[#ff2a2a] hover:bg-[#e01f1f] disabled:opacity-60 text-white text-sm font-semibold py-3 inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="broadcast-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Sending…' : `Send to ${attendees.count} attendee${attendees.count === 1 ? '' : 's'}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
